import type { Alpine as AlpineAPI } from 'alpinejs';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { version } from 'pdfjs-dist/package.json';

const pdfBase = `/js/pdfjs-${version}/`;
type PDFLibrary = typeof import('pdfjs-dist');
let library: Promise<PDFLibrary> | undefined;
function loadPDF() {
    // A variable URL keeps the large parser out of the shared UI bundle.
    library ??= import(pdfBase + 'pdf.min.mjs').catch(error => { library = undefined; throw error; });
    return library;
}

export function registerEvidencePreview(Alpine: AlpineAPI) {
    Alpine.data('evidencePreview', () => {
        // PDF.js objects use private fields and must stay outside Alpine's proxies.
        let document: PDFDocumentProxy | undefined;
        let task: PDFDocumentLoadingTask | undefined;
        let rendering: RenderTask | undefined;
        let request: AbortController | undefined;
        let generation = 0;
        let renderGeneration = 0;
        let observer: ResizeObserver | undefined;
        let resizeTimer: ReturnType<typeof setTimeout> | undefined;
        return {
            filename: '', downloadUrl: '', imageUrl: '', kind: '', error: '',
            busy: false, page: 1, pages: 0, zoom: 1, fitting: true,
            init() {
                let width = 0;
                observer = new ResizeObserver(([entry]) => {
                    if (!entry || entry.contentRect.width === width) return;
                    width = entry.contentRect.width;
                    clearTimeout(resizeTimer);
                    if (width && document && this.fitting) {
                        resizeTimer = setTimeout(() => { void this.renderPage(); }, 100);
                    }
                });
                observer.observe(this.$refs.viewport);
            },
            clear() {
                generation++;
                renderGeneration++;
                clearTimeout(resizeTimer);
                request?.abort(); request = undefined;
                rendering?.cancel(); rendering = undefined;
                // Destroy also terminates the worker and pending document work.
                if (task) void task.destroy().catch(() => {});
                task = undefined; document = undefined;
                if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
                this.imageUrl = ''; this.kind = ''; this.error = ''; this.busy = false;
                this.pages = 0; this.page = 1; this.zoom = 1; this.fitting = true;
                this.$refs.pages.replaceChildren();
            },
            destroy() { this.clear(); observer?.disconnect(); },
            async openEvidence(detail: { auth: string; name?: string }) {
                this.clear();
                const current = generation;
                this.filename = detail.name || 'Evidence file';
                this.downloadUrl = '/medallion-evidence?entry=' + encodeURIComponent(detail.auth);
                this.busy = true;
                window.AB4HUI.open(this.$root);
                request = new AbortController();
                try {
                    const response = await fetch(this.downloadUrl + '&preview=1', { credentials: 'same-origin', cache: 'no-store', signal: request.signal });
                    if (!response.ok) throw new Error(response.status === 403 || response.status === 401
                        ? 'You no longer have access to this file. Refresh the page and sign in again if needed.'
                        : 'The file could not be loaded. Please try again.');
                    const mime = (response.headers.get('Content-Type') || '').split(';')[0]!.trim();
                    const blob = await response.blob();
                    if (current !== generation) return;
                    if (mime === 'application/pdf') {
                        this.kind = 'pdf';
                        const pdf = await loadPDF();
                        const data = new Uint8Array(await blob.arrayBuffer());
                        if (current !== generation) return;
                        pdf.GlobalWorkerOptions.workerSrc = pdfBase + 'pdf.worker.min.mjs';
                        task = pdf.getDocument({ data, cMapUrl: pdfBase + 'cmaps/', cMapPacked: true,
                            standardFontDataUrl: pdfBase + 'standard_fonts/', wasmUrl: pdfBase + 'wasm/' });
                        const loaded = await task.promise;
                        if (current !== generation) return;
                        document = loaded;
                        this.pages = document.numPages;
                        await this.$nextTick();
                        await this.renderPage();
                    } else if (['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(mime)) {
                        this.kind = 'image';
                        this.imageUrl = URL.createObjectURL(blob);
                    } else {
                        throw new Error(mime === 'text/html' ? 'Your session may have expired. Refresh the page and sign in again.'
                            : 'This file type cannot be previewed in the browser.');
                    }
                } catch (error) {
                    if (current !== generation) return;
                    this.busy = false;
                    const failure = error as Error;
                    this.error = failure.name === 'PasswordException' ? 'This PDF is password-protected. Download it to open it with your password.'
                        : failure.name === 'InvalidPDFException' ? 'This PDF could not be read.'
                        : failure.message || 'The preview could not be loaded.';
                }
            },
            async renderPage() {
                if (!document) return;
                const current = ++renderGeneration;
                rendering?.cancel();
                this.busy = true;
                this.error = '';
                try {
                    const pdfPage = await document.getPage(this.page);
                    if (current !== renderGeneration) return;
                    if (this.fitting) this.zoom = Math.min(3, Math.max(0.1, (this.$refs.viewport.clientWidth - 32) / pdfPage.getViewport({ scale: 1 }).width));
                    const viewport = pdfPage.getViewport({ scale: this.zoom });
                    // Bound canvas memory on high-DPI displays and unusually large pages.
                    const density = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(16_000_000 / (viewport.width * viewport.height)));
                    const canvas = window.document.createElement('canvas');
                    canvas.width = Math.max(1, Math.floor(viewport.width * density));
                    canvas.height = Math.max(1, Math.floor(viewport.height * density));
                    canvas.style.width = `${viewport.width}px`;
                    canvas.style.height = `${viewport.height}px`;
                    canvas.setAttribute('role', 'img');
                    canvas.setAttribute('aria-label', `${this.filename}, page ${this.page} of ${this.pages}. Download the original for document text.`);
                    // A fresh canvas prevents overlapping renders after quick resize/page changes.
                    this.$refs.pages.replaceChildren(canvas);
                    rendering = pdfPage.render({ canvas, viewport, transform: [density, 0, 0, density, 0, 0] });
                    await rendering.promise;
                } catch (error) {
                    if (current !== renderGeneration) return;
                    if ((error as Error).name !== 'RenderingCancelledException') this.error = 'This page could not be rendered. Try another page or download the original.';
                } finally {
                    if (current === renderGeneration) { this.busy = false; rendering = undefined; }
                }
            },
            goTo(page: number) {
                if (page < 1 || page > this.pages) return;
                this.page = page;
                this.$refs.viewport.scrollTop = 0;
                void this.renderPage();
            },
            zoomBy(amount: number) { this.fitting = false; this.zoom = Math.max(0.25, Math.min(3, this.zoom + amount)); void this.renderPage(); },
            fitWidth() { this.fitting = true; void this.renderPage(); },
            imageFailed() { this.busy = false; this.error = 'This image could not be displayed in the browser.'; },
        };
    });
}
